// District music (§8): a tiny generated soundtrack per area mood (no audio files). Off by default
// on first launch; master volume in Settings. Crossfades when you walk into a new district.
import { flag } from "@degen/shared/flags";
import { uiPrefs } from "./prefs";

type Mood = "calm" | "upbeat" | "folk" | "party";
const MOOD_OF: Record<string, Mood> = {
  plaza: "calm", park: "calm", home: "calm", lighthouse: "calm", dock: "calm",
  trading: "upbeat", mint: "upbeat", launchpad: "upbeat", missioncontrol: "upbeat", vano: "upbeat", hackhub: "upbeat", gadgets: "upbeat",
  farm: "folk", petshop: "folk", cafe: "folk",
  arena: "party", polancco: "party", abbja: "party", mrjaay: "party", zoomers: "party",
};
const SCALES: Record<Mood, { bpm: number; root: number; steps: number[]; wave: OscillatorType; drums: boolean }> = {
  calm: { bpm: 72, root: 220, steps: [0, 3, 7, 10, 7, 3], wave: "sine", drums: false },
  upbeat: { bpm: 118, root: 196, steps: [0, 7, 12, 7, 10, 7, 3, 7], wave: "square", drums: true },
  folk: { bpm: 96, root: 247, steps: [0, 2, 4, 7, 9, 7, 4, 2], wave: "triangle", drums: false },
  party: { bpm: 124, root: 165, steps: [0, 12, 7, 12, 10, 12, 7, 5], wave: "sawtooth", drums: true },
};

let ctx: AudioContext | null = null;
let out: GainNode | null = null;
let mood: Mood | null = null;
let timer: ReturnType<typeof setInterval> | null = null;
let step = 0;
let ducked = false; // the car radio is playing
const level = () => (ducked ? 0 : 0.35 * uiPrefs.value.volume);

function ensure() {
  if (ctx) return ctx;
  try {
    ctx = new AudioContext();
  } catch {
    return null;
  }
  out = ctx.createGain();
  out.gain.value = 0;
  out.connect(ctx.destination);
  return ctx;
}

function note(freq: number, at: number, len: number, wave: OscillatorType, vol: number) {
  const c = ctx!;
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = wave;
  o.frequency.value = freq;
  g.gain.setValueAtTime(0, at);
  g.gain.linearRampToValueAtTime(vol, at + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, at + len);
  const lp = c.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = 1400;
  o.connect(lp).connect(g).connect(out!);
  o.start(at);
  o.stop(at + len + 0.05);
}

function hat(at: number) {
  const c = ctx!;
  const b = c.createBuffer(1, c.sampleRate * 0.05, c.sampleRate);
  const d = b.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / d.length);
  const s = c.createBufferSource();
  s.buffer = b;
  const hp = c.createBiquadFilter();
  hp.type = "highpass";
  hp.frequency.value = 7000;
  const g = c.createGain();
  g.gain.value = 0.05;
  s.connect(hp).connect(g).connect(out!);
  s.start(at);
}

function play(m: Mood) {
  const sc = SCALES[m];
  if (timer) clearInterval(timer);
  const beat = 60 / sc.bpm / 2;
  timer = setInterval(() => {
    if (!ctx || !out) return;
    const t = ctx.currentTime + 0.05;
    const semis = sc.steps[step % sc.steps.length];
    note(sc.root * 2 ** (semis / 12), t, beat * 1.6, sc.wave, 0.05);
    if (step % 4 === 0) note(sc.root / 2, t, beat * 3.5, "sine", 0.08); // bass
    if (sc.drums && step % 2 === 1) hat(t);
    step++;
  }, beat * 1000);
}

/** Call when the player's zone changes. */
export function setMusicZone(zoneId: string | null) {
  const want = uiPrefs.value.music && flag("music") ? (MOOD_OF[zoneId ?? "plaza"] ?? mood ?? "calm") : null;
  if (!want) {
    if (out && ctx) out.gain.setTargetAtTime(0, ctx.currentTime, 0.4);
    if (timer) clearInterval(timer);
    timer = null;
    mood = null;
    return;
  }
  if (want === mood) return;
  const c = ensure();
  if (!c) return;
  void c.resume();
  mood = want;
  out!.gain.setTargetAtTime(0, c.currentTime, 0.3); // crossfade out, then in with the new mood
  setTimeout(() => {
    play(want);
    out!.gain.setTargetAtTime(level(), c.currentTime + 0.3, 0.8);
  }, 400);
}

export function setMusicVolume() {
  if (out && ctx && mood) out.gain.setTargetAtTime(level(), ctx.currentTime, 0.2);
}

/** Fade the district music out while the car radio plays (and back in after). */
export function duckMusic(on: boolean) {
  ducked = on;
  setMusicVolume();
}
